/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Get started with Match Booster", description = "Set up Match Booster in two steps: configure enrichment on a model, then enable it in your syncs.", lastUpdatedDate = new Date(1778803200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Get started with Match Booster",
        description: "Set up Match Booster in two steps: configure enrichment on a model, then enable it in your syncs.",
        lastUpdatedDate: "Fri May 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Step 1: Configure enrichment on a model",
          "depth": 0,
          "id": "step-1-configure-enrichment-on-a-model"
        }, {
          "value": "Step 2: Enable Match Booster in your syncs",
          "depth": 0,
          "id": "step-2-enable-match-booster-in-your-syncs"
        }, {
          "value": "Monitor match rates",
          "depth": 0,
          "id": "monitor-match-rates"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      hr: "hr",
      h2: "h2",
      strong: "strong",
      ol: "ol",
      li: "li",
      a: "a"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: " as an add-on product."
      }), "\n", _jsx(_components.p, {
        children: "Setting up Match Booster is a two-step process. Both steps are required — enrichment must complete before syncs can send boosted identifiers."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "step-1-configure-enrichment-on-a-model",
        children: "Step 1: Configure enrichment on a model"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Who:"
        }), " Data teams or platform admins"]
      }), "\n", _jsx(_components.p, {
        children: "Before enabling Match Booster, confirm that your model or parent model includes at least one supported identifier (email, phone, MAID, IP address, or name + postal address)."
      }), "\n", _jsxs(_components.p, {
        children: ["Then, on the model's ", _jsx(_components.strong, {
          children: "Match Booster"
        }), " tab:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enable Match Booster and map your identifiers."
        }), "\n", _jsx(_components.li, {
          children: "Optionally enable Household Expansion or CTV Matching."
        }), "\n", _jsx(_components.li, {
          children: "Set the enrichment schedule."
        }), "\n", _jsx(_components.li, {
          children: "Run the initial enrichment (one-time, can take up to 72 hours)."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Wait for the initial enrichment to complete before moving to Step 2. Select\n", _jsx(_components.strong, {
            children: "Email me when enrichment is complete"
          }), " during initialization so you don't\nhave to check manually."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/match-booster/configure-model",
          children: "Configure Match Booster on a model"
        }), " for the full walkthrough."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "step-2-enable-match-booster-in-your-syncs",
        children: "Step 2: Enable Match Booster in your syncs"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Who:"
        }), " Marketers or platform admins"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once enrichment has completed, open a sync to a ", _jsx(_components.a, {
          href: "/match-booster/overview#supported-destinations",
          children: "supported destination"
        }), " and:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Toggle ", _jsx(_components.strong, {
            children: "Enable Match Booster"
          }), " on in the sync's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab."]
        }), "\n", _jsx(_components.li, {
          children: "Choose a targeting level: Person, Household, or CTV."
        }), "\n", _jsx(_components.li, {
          children: "Review the mapped identifiers — Hightouch automatically selects the best available enriched identifiers for the destination."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/match-booster/enable-in-syncs",
          children: "Enable Match Booster in syncs"
        }), " for the full walkthrough."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "monitor-match-rates",
        children: "Monitor match rates"
      }), "\n", _jsxs(_components.p, {
        children: ["After a sync runs with Match Booster enabled, check match rates on the sync's ", _jsx(_components.strong, {
          children: "Overview"
        }), " tab under ", _jsx(_components.strong, {
          children: "Matched users"
        }), " to assess the impact of enrichment."]
      }), "\n", _jsx(_components.p, {
        children: "Match rates can take up to 48 hours to stabilize after the initial sync, depending on the ad platform."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/match-booster/enable-in-syncs#monitor-match-rates",
          children: "Monitor match rates"
        }), " for details."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
